import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Plus, Target, TrendingUp, Calendar as CalendarIcon, AlertCircle, Lightbulb, X } from 'lucide-react';
import { toast } from 'sonner';
import CreateSavingsGoalWizard from './CreateSavingsGoalWizard';
import SavingsGoalCard from './SavingsGoalCard';
import useTips from '../hooks/useTips';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

// Coach tip for savings goals - Premium & Dismissable
const SavingsCoachTip = ({ onDismiss }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    className="premium-card relative mb-5"
  >
    {/* Close button */}
    {onDismiss && (
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-muted transition-colors z-10"
        aria-label="Masquer cette astuce"
      >
        <X className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
      </button>
    )}
    
    <div className="flex items-start gap-3 pr-8">
      <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
        <Lightbulb className="h-5 w-5 text-emerald-600" strokeWidth={1.5} />
      </div>
      <div className="space-y-2 flex-1">
        <h3 className="text-sm font-semibold text-foreground tracking-tight">
          Comment fonctionne un objectif d'épargne ?
        </h3>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Un objectif t'aide à <strong className="text-foreground">épargner régulièrement</strong> pour réaliser un projet qui te tient à cœur.
        </p>
        <div className="bg-muted/30 rounded-lg p-2.5 text-xs text-foreground/70">
          <p>
            <span className="mr-1.5">✈️</span>
            <strong className="text-emerald-600">Exemple :</strong> Tu rêves d'un voyage à 1500€ ? Crée un objectif "Voyage" et mets 100€ de côté chaque mois. En 15 mois, tu y es !
          </p>
        </div>
      </div>
    </div>
  </motion.div>
);

// Configuration pour limiter les requêtes simultanées
const CONCURRENT_REQUESTS_LIMIT = 10; // Max 10 requêtes simultanées
const REQUEST_TIMEOUT_MS = 8000; // Timeout de 8 secondes par requête

// Helper: Exécuter des promesses par lots avec limite de concurrence
const executeInBatches = async (items, asyncFn, batchSize = CONCURRENT_REQUESTS_LIMIT) => {
  const results = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(asyncFn));
    results.push(...batchResults);
  }
  return results;
};

// Helper: Requête avec timeout
const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  
  try {
    const response = await axios.get(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

const SavingsGoals = ({ calendar, onDataChanged }) => {
  const [goals, setGoals] = useState([]);
  const [goalsProgress, setGoalsProgress] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0 });
  
  // Tips system
  const { tips, dismissTip } = useTips('objectifs');
  const showIntroTip = tips.some(t => t.id === 'objectifs_intro');

  // Charger goals ET progress avec affichage progressif
  const loadAllData = async () => {
    if (!calendar?.id) return;
    
    setLoading(true);
    setLoadingProgress({ loaded: 0, total: 0 });
    
    try {
      // 1. Charger les objectifs (affichage immédiat possible)
      const goalsResponse = await axios.get(
        `${API}/savings-goals?calendar_id=${calendar.id}`,
        { withCredentials: true }
      );
      const goalsData = goalsResponse.data || [];
      
      // Afficher immédiatement les objectifs (sans progress)
      setGoals(goalsData);
      setLoadingProgress({ loaded: 0, total: goalsData.length });
      
      // 2. Charger le progress EN PARALLÈLE avec limitation de concurrence
      if (goalsData.length > 0) {
        let loadedCount = 0;
        const progressData = {};
        
        // Fonction de récupération avec gestion d'erreur robuste
        const fetchProgress = async (goal) => {
          try {
            const response = await fetchWithTimeout(
              `${API}/savings-goals/${goal.id}/progress`,
              { withCredentials: true }
            );
            loadedCount++;
            setLoadingProgress(prev => ({ ...prev, loaded: loadedCount }));
            return { goalId: goal.id, data: response.data, success: true };
          } catch (error) {
            // Gestion silencieuse des erreurs individuelles
            console.warn(`Failed to load progress for goal ${goal.id}:`, error.message);
            loadedCount++;
            setLoadingProgress(prev => ({ ...prev, loaded: loadedCount }));
            return { goalId: goal.id, data: null, success: false };
          }
        };
        
        // Exécuter par lots pour éviter la surcharge serveur
        const progressResults = await executeInBatches(goalsData, fetchProgress);
        
        // Construire les données de progress (ignorer les échecs)
        progressResults.forEach(({ goalId, data, success }) => {
          if (success && data) {
            progressData[goalId] = data;
          }
        });
        
        setGoalsProgress(progressData);
      }
    } catch (error) {
      console.error('Error loading savings goals:', error);
      // En cas d'erreur globale, afficher liste vide mais ne pas bloquer
      setGoals([]);
    } finally {
      setLoading(false);
      setLoadingProgress({ loaded: 0, total: 0 });
    }
  };

  useEffect(() => {
    loadAllData();
  }, [calendar]);

  // Recharger le progress quand les goals changent (après création/modification)
  const refreshProgress = async () => {
    if (goals.length === 0) return;
    
    setProgressLoading(true);
    try {
      // Fonction de récupération avec gestion d'erreur robuste
      const fetchProgress = async (goal) => {
        try {
          const response = await fetchWithTimeout(
            `${API}/savings-goals/${goal.id}/progress`,
            { withCredentials: true }
          );
          return { goalId: goal.id, data: response.data, success: true };
        } catch (error) {
          console.warn(`Failed to refresh progress for goal ${goal.id}:`, error.message);
          return { goalId: goal.id, data: null, success: false };
        }
      };
      
      // Exécuter par lots
      const progressResults = await executeInBatches(goals, fetchProgress);
      const progressData = {};
      
      progressResults.forEach(({ goalId, data, success }) => {
        if (success && data) {
          progressData[goalId] = data;
        }
      });
      
      setGoalsProgress(progressData);
    } catch (error) {
      console.error('Error refreshing progress:', error);
    } finally {
      setProgressLoading(false);
    }
  };

  const handleDismissTip = async () => {
    const success = await dismissTip('objectifs_intro', 'objectifs');
    if (success) {
      toast.success('Astuce masquée - retrouve-la dans l\'onglet Astuces');
    }
  };

  const handleGoalCreated = () => {
    setShowCreateDialog(false);
    loadAllData(); // Recharger tout proprement
    toast.success('Bravo ! Ton objectif est créé. Tu es sur la bonne voie !');
    // Trigger calendar refresh for immediate sync
    if (onDataChanged) onDataChanged();
  };

  const handleGoalDeleted = () => {
    loadAllData(); // Recharger tout proprement
    toast.success('Objectif supprimé');
    // Trigger calendar refresh for immediate sync
    if (onDataChanged) onDataChanged();
  };

  const handleGoalUpdated = () => {
    refreshProgress(); // Juste rafraîchir le progress
    if (onDataChanged) onDataChanged();
  };

  // État de chargement initial avec indicateur de progression
  if (loading && goals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <motion.div 
          className="w-10 h-10 border-3 border-emerald-500 border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
        {loadingProgress.total > 0 && (
          <p className="text-xs text-muted-foreground">
            Chargement {loadingProgress.loaded}/{loadingProgress.total}...
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Coach Tip - Dismissable */}
      <AnimatePresence>
        {showIntroTip && (
          <SavingsCoachTip onDismiss={handleDismissTip} />
        )}
      </AnimatePresence>

      {/* Header - Premium */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h2 className="text-lg font-semibold text-foreground tracking-tight flex items-center gap-2">
            <Target className="h-5 w-5 text-emerald-600" strokeWidth={1.5} />
            Mes Objectifs
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Réalise tes rêves, un euro à la fois
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="gap-1.5 bg-emerald-500 hover:bg-emerald-600 h-10 px-4 rounded-xl"
          data-testid="create-savings-goal-button"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          <span className="text-sm">Nouveau</span>
        </Button>
      </motion.div>

      {/* Goals List */}
      {goals.length === 0 ? (
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="premium-card text-center py-10"
        >
          <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Target className="h-10 w-10 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold text-emerald-800 mb-2">
            Prêt à réaliser tes rêves ? 🌟
          </h3>
          <p className="text-sm text-gray-600 mb-6 max-w-sm mx-auto">
            Crée ton premier objectif d'épargne et commence à construire ton avenir. 
            Chaque petit pas compte !
          </p>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            size="lg"
          >
            <Plus className="h-5 w-5" />
            Créer mon premier objectif
          </Button>
        </motion.div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-6">
            {goals.map((goal) => (
              <SavingsGoalCard
                key={goal.id}
                goal={goal}
                progress={goalsProgress[goal.id]}
                currency={calendar.currency}
                onDeleted={handleGoalDeleted}
                onUpdated={handleGoalUpdated}
              />
            ))}
          </div>

          {/* Message inspirant réunionnais */}
          <div className="mt-12 text-center">
            <div className="inline-block bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl px-8 py-6 border-2 border-teal-200">
              <p className="text-2xl font-bold text-teal-700 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                "Ti pa ti pa, l'aventure commence ici, sur notre île intense."
              </p>
              <p className="text-sm text-gray-600">
                🏝️ Chaque effort compte - Reste motivé, le génie veille sur tes rêves
              </p>
            </div>
          </div>
        </>
      )}


      <CreateSavingsGoalWizard
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        calendarId={calendar.id}
        currentBalance={0}
        onSuccess={handleGoalCreated}
      />
    </div>
  );
};

export default SavingsGoals;